import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  isWidgetHideable,
  isWidgetPinned,
  type WidgetLayout,
  type WidgetSurface,
} from "@heraion/shared";

import { Button } from "../primitives/Button";
import { InlineStatusHint } from "../primitives/InlineStatusHint";
import { PageSheet } from "../primitives/PageSheet";
import { SectionHeader } from "../primitives/SectionHeader";
import { helpTextStyle } from "../primitives/helpTextStyle";
import type { UiTheme } from "../theme";
import { useBreakpoint } from "../useBreakpoint";
import { widgetColumnStyles } from "./WidgetColumns";
import { WidgetConfigColumn } from "./WidgetConfigColumn";
import { WidgetConfigRow } from "./WidgetConfigRow";
import { useSortableDrag } from "./useSortableDrag";

type Column = "main" | "rail";

// HR-11: the page that configures a widget surface (Antenore 27/09: "hidden
// cards below in Inactive; drag and drop; ✕ removes, it goes down; ＋ adds, it
// goes back up"). Opened only from «Customize» since PG-63 (29/09: no hold on
// the cards). Changes apply live, so the header carries only Close.
// Wide Home (fetta 2, 27/09): two lists side by side, main and rail, as the
// Home lays them out; `columnOf` picks the column. Inactive stays full width.
// PG-57: Home and Progress are the two callers. Progress also passes
// `onSetCollapsed`, its third state; Home does not, so its rows stay as they
// were. PG-64: the report is the third; it lists its premium sections
// under `locked`, shown and never addable.
export function WidgetConfigSheet<Id extends string>({
  theme,
  visible,
  onClose,
  title,
  saveErrorText,
  surface,
  labelOf,
  columnOf,
  layout,
  onMove,
  onHide,
  onShow,
  onSetCollapsed,
  onReset,
  isDefault,
  saveFailed,
  topInset,
  bottomInset,
  locked,
}: {
  theme: UiTheme;
  visible: boolean;
  onClose: () => void;
  title: string;
  saveErrorText: string;
  surface: WidgetSurface<Id>;
  labelOf: (id: Id) => string;
  /** Wide split into main and rail. Absent: one list at every width. */
  columnOf?: (id: Id) => Column;
  layout: WidgetLayout<Id>;
  /** Move `id` to `toIndex` of `layout.order`. */
  onMove: (id: Id, toIndex: number) => void;
  onHide: (id: Id) => void;
  onShow: (id: Id) => void;
  /** The collapsed state. Absent: the surface has none. */
  onSetCollapsed?: (id: Id, collapsed: boolean) => void;
  onReset: () => void;
  isDefault: boolean;
  saveFailed: boolean;
  topInset?: number;
  bottomInset?: number;
  /** Shown under Premium, with no way to add them. Absent: no such group. */
  locked?: { ids: readonly Id[]; title: string; hint: string };
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const { wide } = useBreakpoint();
  const split = wide && columnOf !== undefined;
  const pinned = layout.order.filter((id) => isWidgetPinned(surface, id));
  const movable = layout.order.filter((id) => !isWidgetPinned(surface, id));
  const inColumn = (c: Column) => (id: Id) => columnOf?.(id) === c;
  const mainIds = split ? movable.filter(inColumn("main")) : movable;
  const railIds = split ? movable.filter(inColumn("rail")) : [];
  // A slot of a column list lands where that slot's widget sits in the ONE
  // order list, so both layouts save the same kind of order.
  const moveTo = (list: Id[]) => (id: Id, slot: number) => {
    const at = list[slot];
    if (at) onMove(id, layout.order.indexOf(at));
  };
  const sortable = (list: Id[]) => ({
    count: list.length,
    onDrop: (from: number, to: number) => {
      const id = list[from];
      if (id) moveTo(list)(id, to);
    },
  });
  const mainDrag = useSortableDrag(sortable(mainIds));
  const railDrag = useSortableDrag(sortable(railIds));
  const dragging =
    mainDrag.draggingIndex !== null || railDrag.draggingIndex !== null;
  const columnTitle = {
    main: t("mobile.home.config.columnMain", { defaultValue: "Main column" }),
    rail: t("mobile.home.config.columnRail", { defaultValue: "Side column" }),
  };
  const column = (c: Column, ids: Id[], drag: typeof mainDrag) => (
    <WidgetConfigColumn
      theme={theme}
      title={split ? columnTitle[c] : undefined}
      pinned={split ? pinned.filter(inColumn(c)) : pinned}
      movable={ids}
      drag={drag}
      labelOf={labelOf}
      isHideable={(id) => isWidgetHideable(surface, id)}
      onMoveTo={moveTo(ids)}
      onHide={onHide}
      collapsed={onSetCollapsed ? layout.collapsed : undefined}
      onSetCollapsed={onSetCollapsed}
      style={split ? widgetColumnStyles[c] : null}
    />
  );

  return (
    <PageSheet
      theme={theme}
      visible={visible}
      onRequestClose={onClose}
      title={title}
      cancelLabel={t("mobile.common.close", { defaultValue: "Close" })}
      topInset={topInset}
      bottomInset={bottomInset}
    >
      <ScrollView
        contentContainerStyle={styles.body}
        scrollEnabled={!dragging}
      >
        <SectionHeader
          theme={theme}
          title={t("mobile.home.config.active", { defaultValue: "Active" })}
        />
        <Text style={styles.hint}>
          {t("mobile.home.config.dragHint", {
            defaultValue: "Drag a card by its handle to reorder.",
          })}
        </Text>
        {split ? (
          <View style={[widgetColumnStyles.cols, styles.colsGap]}>
            {column("main", mainIds, mainDrag)}
            {column("rail", railIds, railDrag)}
          </View>
        ) : (
          column("main", mainIds, mainDrag)
        )}

        <SectionHeader
          theme={theme}
          title={t("mobile.home.config.inactive", { defaultValue: "Inactive" })}
          style={styles.sectionGap}
        />
        {layout.hidden.length === 0 ? (
          <Text style={styles.hint}>
            {t("mobile.home.config.inactiveEmpty", {
              defaultValue: "Nothing hidden. Tap ✕ on a card to hide it.",
            })}
          </Text>
        ) : (
          layout.hidden.map((id) => (
            <WidgetConfigRow
              key={id}
              kind="inactive"
              theme={theme}
              label={labelOf(id)}
              onShow={() => onShow(id)}
            />
          ))
        )}

        {locked && locked.ids.length > 0 ? (
          <>
            <SectionHeader
              theme={theme}
              title={locked.title}
              style={styles.sectionGap}
            />
            <Text style={styles.hint}>{locked.hint}</Text>
            {locked.ids.map((id) => (
              <WidgetConfigRow
                key={id}
                kind="locked"
                theme={theme}
                label={labelOf(id)}
              />
            ))}
          </>
        ) : null}

        {saveFailed ? (
          <InlineStatusHint theme={theme} tone="error" text={saveErrorText} />
        ) : null}
        <Button
          theme={theme}
          variant="ghost"
          label={t("mobile.home.config.reset", {
            defaultValue: "Reset to default",
          })}
          onPress={onReset}
          disabled={isDefault}
          style={styles.reset}
        />
      </ScrollView>
    </PageSheet>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    body: { padding: s.s5, paddingTop: s.s2 },
    hint: { ...helpTextStyle(theme), marginTop: s.s2, marginBottom: s.s3 },
    sectionGap: { marginTop: s.s5 },
    // The surface's own split (widgetColumnStyles), plus a gap: these rows
    // carry no side margin of their own.
    colsGap: { gap: s.s5 },
    reset: { alignSelf: "center", marginTop: s.s5 },
  });
}
